import './JobInfoForm.scss';
import { faArrowLeft, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TranslationKey, useTranslator } from '@Localization';
import { AutocompleteOption, ListItemContent, ListItemDecorator } from '@mui/joy';
import { compact } from 'lodash/fp';
import { observer } from 'mobx-react-lite';
import { ReactElement, useMemo } from 'react';
import { ValidatedSelectInput } from '../../../../__legacy__/components/ValidatedSelectInput';
import { ValidatedTextArea } from '../../../../__legacy__/components/ValidatedTextArea';
import { ValidatedTextInput } from '../../../../__legacy__/components/ValidatedTextInput';
import { Home } from '../../../../__legacy__/routes/config/Home';
import { S_Alert } from '../../../../design/alert/S_Alert';
import { S_Link } from '../../../../design/links/S_Link';
import { ColumnLayout } from '../../../common/layouts/columnLayout/ColumnLayout';
import { IJobDetailsStore } from '../../jobDetails.store';
import { INFERENCE_TYPE, JOB_STATUS, JOB_TYPE, TRAINING_TYPE, TRAINING_VARIANT, TRAINING_VARIANTS_LIST } from '../../jobs.model';
import { getJobTranslationPrefix } from '../../utils';
import { JobTypeRadios } from './JobTypeRadios';

interface IJobInfoFormProps {
  workspaceId: string;
  job: IJobDetailsStore['job'];
  jobType: JOB_TYPE;
  dataSources: IJobDetailsStore['dataSources'];
  validationErrors: IJobDetailsStore['validationErrors'];
  isFormDisabled: boolean;
  handleChangeProject: (projectId: string) => Promise<void>;
  handleBlurProject: (projectId: string) => void;
  handleChangeName: (name: string) => void;
  handleBlurName: (name: string) => Promise<void>;
  handleChangeDescription: (description: string) => void;
  handleBlurDescription: (description: string) => Promise<void>;
  handleChangeTrainingVariant: (value: TRAINING_VARIANT | null) => Promise<void>;
  handleBlurTrainingVariant: (trainingVariant: TRAINING_VARIANT | null) => void;
  handleChangeDataset: (datasetId: string) => Promise<void>;
  handleBlurDataset: (datasetId: string) => void;
  handleChangeTraining: (trainingId: string) => void;
  handleBlurTraining: (traininigId: string) => void;
  handleChangeJobType: (type: TRAINING_TYPE | INFERENCE_TYPE) => Promise<void>;
}

export const JobInfoForm = observer((props: IJobInfoFormProps): ReactElement => {
  const {
    workspaceId,
    job: { name, description, status, projectId, variant, type, parentTrainingId, datasetIds, trainingId },
    jobType,
    validationErrors,
    dataSources,
    isFormDisabled,
    handleBlurProject,
    handleBlurName,
    handleChangeProject,
    handleChangeName,
    handleChangeDescription,
    handleBlurDescription,
    handleBlurTrainingVariant,
    handleChangeTrainingVariant,
    handleChangeJobType,
    handleBlurDataset,
    handleChangeDataset,
    handleBlurTraining,
    handleChangeTraining,
  } = props;
  const { t } = useTranslator();
  const translationPrefix = getJobTranslationPrefix(jobType);
  const isDisabled = isFormDisabled || (status !== JOB_STATUS.DRAFT && status !== JOB_STATUS.STOPPED && status !== JOB_STATUS.FAILED);
  const validationError = (label: string): { message: TranslationKey } | null | undefined => validationErrors.header.get(label);
  const isFieldValidated = (label: string): boolean => validationError(label) !== undefined || validationErrors.showAll;
  const hasError = (label: string): boolean => !!validationError(label);

  const variantOptions = TRAINING_VARIANTS_LIST.map((variant) => ({
    value: variant,
    label: t(`training_variants.${variant}`),
  }));

  const [trainingOptions, trainingLookup] = useMemo(
    () =>
      dataSources.trainings.reduce<[{ value: string; label: string }[], Record<string, { value: string; label: string }>]>(
        ([options, lookup], d) => {
          lookup[d.value] = d;

          return [options, lookup];
        },
        [dataSources.trainings, {}]
      ),
    [dataSources.trainings]
  );

  const isTraining = jobType === JOB_TYPE.TRAINING;

  const trainingSelect = (
    <>
      <div className='training-label'>{t('training_label')}</div>
      <ValidatedSelectInput
        id='training-dropdown'
        className='training-dropdown'
        value={trainingId ? trainingLookup[trainingId] : undefined}
        onChange={(_, v) => {
          handleChangeTraining(v.value ?? '');
        }}
        onBlur={
          trainingId
            ? (): void => {
                handleBlurTraining(trainingId);
              }
            : undefined
        }
        disabled={isDisabled}
        isValid={isFieldValidated('training') ? !hasError('training') : undefined}
        feedbacks={compact(hasError('training') ? [validationError('training')?.message] : [])}
        feedbackParams={hasError('training') ? { name: t('fields.training') } : {}}
        options={trainingOptions}
      />
    </>
  );

  const selectedDataset = dataSources.datasetsInWorkspace.filter((x) => datasetIds.some((d) => d === x.id))[0];

  const datasetSelect = (
    <>
      <div className='dataset-label'>{t('dataset')}</div>
      <ValidatedSelectInput
        id='dataset-dropdown'
        className='dataset-dropdown'
        value={selectedDataset}
        onChange={(_, v) => {
          void handleChangeDataset(v.id ?? '');
        }}
        onBlur={
          datasetIds.length
            ? (): void => {
                handleBlurDataset(datasetIds[0]);
              }
            : undefined
        }
        disabled={isDisabled}
        isValid={isFieldValidated('dataset') ? !hasError('dataset') : undefined}
        feedbacks={compact(hasError('dataset') ? [validationError('dataset')?.message] : [])}
        feedbackParams={hasError('dataset') ? { name: t('fields.dataset') } : {}}
        options={dataSources.datasetsInWorkspace}
        endDecorator={
          (selectedDataset?.sizeInMB ?? 0) > 1024 ? (
            <FontAwesomeIcon
              className='menu-warning-icon'
              icon={faExclamationTriangle}
              title={t('project_size_limit_training')}
            />
          ) : undefined
        }
        getOptionLabel={(x) => x.name}
        renderOption={(props, option) => (
          <AutocompleteOption {...props}>
            <ListItemContent>{option.name}</ListItemContent>
            <ListItemDecorator>
              {option.sizeInMB > 1024 && (
                <FontAwesomeIcon
                  className='menu-warning-icon'
                  icon={faExclamationTriangle}
                  title={t('project_size_limit_training')}
                />
              )}
            </ListItemDecorator>
          </AutocompleteOption>
        )}
      />
    </>
  );

  const selectedProject = dataSources.projects.find((x) => x.value === projectId);

  const projectSelect = (
    <>
      <div className='project-label'>{t('project')}</div>
      <ValidatedSelectInput
        id='project-dropdown'
        className='project-dropdown'
        value={selectedProject}
        onChange={(_, v) => {
          void handleChangeProject(v.value ?? '');
        }}
        onBlur={
          projectId !== null
            ? (): void => {
                handleBlurProject(projectId);
              }
            : undefined
        }
        disabled={isDisabled}
        isValid={isFieldValidated('project') ? !hasError('project') : undefined}
        feedbacks={compact(hasError('project') ? [validationError('project')?.message] : [])}
        feedbackParams={hasError('project') ? { name: t('fields.project') } : {}}
        options={dataSources.projects}
        endDecorator={
          (selectedProject?.sizeInMB ?? 0) > 1024 ? (
            <FontAwesomeIcon
              className='menu-warning-icon'
              icon={faExclamationTriangle}
              title={t('project_size_limit_training')}
            />
          ) : undefined
        }
        renderOption={(props, option) => (
          <AutocompleteOption {...props}>
            <ListItemContent>{option.label}</ListItemContent>
            <ListItemDecorator>
              {option.sizeInMB > 1024 && (
                <FontAwesomeIcon
                  className='menu-warning-icon'
                  icon={faExclamationTriangle}
                  title={t('project_size_limit_training')}
                />
              )}
            </ListItemDecorator>
          </AutocompleteOption>
        )}
      />
    </>
  );

  return (
    <ColumnLayout
      size='md'
      className='job-info-form'
    >
      {!!parentTrainingId && (
        <div>
          <S_Link
            title={t('training.back.title')}
            color='anchor'
            className='link-flex with-margin'
            to={Home.Trainings.Details.TrainingJobs.withParams({ workspaceId, jobId: parentTrainingId })}
          >
            <FontAwesomeIcon icon={faArrowLeft} />
            {t('training.back.description')}
          </S_Link>
          <S_Alert color='info'>
            <strong>{t('info')} </strong>
            {t('training.sub_training_info')}
          </S_Alert>
        </div>
      )}
      <ValidatedTextInput
        id='job-name-textbox'
        type='text'
        placeholder={t(`${translationPrefix}.name.placeholder`)}
        value={name}
        onChange={handleChangeName}
        onBlur={(name) => {
          void handleBlurName(name);
        }}
        isValid={isFieldValidated('name') ? !hasError('name') : undefined}
        feedbacks={compact(hasError('name') ? [validationError('name')?.message] : [])}
        feedbacksParams={hasError('name') ? { name: t('fields.training_name') } : {}}
        disabled={!!parentTrainingId}
      />

      <ValidatedTextArea
        id='job-name-textbox'
        placeholder={t(`${translationPrefix}.description.placeholder`)}
        value={description}
        onChange={handleChangeDescription}
        onBlur={(desc) => {
          void handleBlurDescription(desc);
        }}
        isValid={isFieldValidated('description') ? !hasError('description') : undefined}
        disabled={!!parentTrainingId}
      />

      <div className='job-info-inputs'>
        {isTraining && projectSelect}
        <div className='type-label'>{t('type')}</div>
        <ValidatedSelectInput
          id='type-dropdown'
          className='type-dropdown'
          value={variantOptions.find((x) => x.value === variant)}
          onChange={(_, v) => {
            void handleChangeTrainingVariant(v.value);
          }}
          onBlur={() => {
            handleBlurTrainingVariant(variant);
          }}
          disabled={isDisabled}
          isValid={isFieldValidated('variant') ? !hasError('variant') : undefined}
          feedbacks={compact(hasError('variant') ? [validationError('variant')?.message] : [])}
          feedbackParams={hasError('variant') ? { name: t('fields.variant') } : {}}
          options={variantOptions}
        />
        {!isTraining && trainingSelect}
      </div>
      {!parentTrainingId && (
        <JobTypeRadios
          handleChangeJobType={handleChangeJobType}
          isDisabled={isDisabled}
          isTraining={isTraining}
          type={type}
        />
      )}
      <div className='job-info-inputs'>
        {type === INFERENCE_TYPE.PROJECT && projectSelect}
        {type === INFERENCE_TYPE.DATASET && datasetSelect}
      </div>
    </ColumnLayout>
  );
});
